/*
<summary>
   This class component is all about Managing Crimping Inspection Record data.
   Developer: Aashish Singh, Created Date: 24-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { errorMessage } from '../../constants/error-constants';
import URLConstants from '../../constants/url-constants';
import { IAddCrimpToolInspectionRequest } from '../../models/forms/IAddCrimpToolInspection';
import { ISharedInspectionFilter } from '../../models/ISharedInspectionFilter';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { IToolCrimpInspection, IToolCrimpInspectionListVM } from '../../models/response/IToolCrimpInspectionResponse';
import { downloadCsvFromBase64 } from './utils/download-csv';
import { buildInspectionFilterQuery } from './utils/inspection-filter-query';
import preferencesStore from './preferences-store';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';
import { formatMessage } from '../../translations/format-message';
import {
  getToolCrimpInspectionListInitialState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';

export class CrimpingInspectionRecordStore {
  initialStateValue = ObservableInitialState;
  exportCrimpingInspectionRecordFileState = { ...this.initialStateValue };

  toolCrimpInspectionList: IToolCrimpInspectionListVM =
    getToolCrimpInspectionListInitialState;
  getAllToolCrimpInspectionState = { ...this.initialStateValue };

  toolCrimpInspectionLast24Hours: IToolCrimpInspection[] = [];
  getCrimpToolInspectionLast24HoursState = { ...this.initialStateValue };
  addCrimpToolInspectionState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      exportCrimpingInspectionRecordFileState: observable,
      toolCrimpInspectionList: observable,
      getAllToolCrimpInspectionState: observable,
      toolCrimpInspectionLast24Hours: observable,
      getCrimpToolInspectionLast24HoursState: observable,
      addCrimpToolInspectionState: observable,
      ExportCrimpingInspectionRecordFileService: action,
      GetToolCrimpInspectionListService: action,
      GetCrimpToolInspectionLast24HoursService: action,
      AddCrimpToolInspectionService: action,
      resetExportFileState: action,
      resetAddCrimpToolInspectionState: action,
      reset: action,
      getToolCrimpInspectionList: computed,
      resetStore: action,
      resetToolCrimpInspectionList: action,
    });
  }

  /**
  * This function is used to get the crimp tool inspection list API
  * @param currentPage: Current page number
  * @param pageLimit: Number of records per page
  * @param filter: Shared inspection filter values (Owner Factory, Tool No, Tool Name,
  * Inspection Date range, Judgment Result)
  */
  GetToolCrimpInspectionListService = (
    currentPage: number,
    pageLimit: number,
    filter?: ISharedInspectionFilter
  ) => {
    this.getAllToolCrimpInspectionState.inProgress = true;
    const url = `${URLConstants.GetCrimpToolInspectionList}?PageNo=${currentPage}&PageSize=${pageLimit}${buildInspectionFilterQuery(filter)}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolCrimpInspectionListVM>>) => {
        if (response.data.Error) {
          this.getAllToolCrimpInspectionState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.toolCrimpInspectionList = response.data.Data;
        }
      })
      .catch((err:string) => {
        this.getAllToolCrimpInspectionState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.getAllToolCrimpInspectionState.inProgress = false;
        })
      );
  };

  /**
  * This computed property is used to map toolCrimpInspectionList to the list suitable for
  * the Grid Component.
  */
  get getToolCrimpInspectionList() {
    return this.toolCrimpInspectionList?.CrimpInspection || [];
  }

  /**
  * This function is used to get a tool's crimp inspection records from the last 24 hours from API.
  * @param toolId: Tool Id to look up
  */
  GetCrimpToolInspectionLast24HoursService = (toolId: number) => {
    this.getCrimpToolInspectionLast24HoursState.inProgress = true;
    const url = `${URLConstants.GetCrimpToolInspectionLast24Hours}?toolId=${toolId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolCrimpInspection[]>>) => {
        if (response.data.Error) {
          this.toolCrimpInspectionLast24Hours = [];
        } else {
          this.toolCrimpInspectionLast24Hours = response.data.Data || [];
        }
      })
      .catch(() => {
        this.toolCrimpInspectionLast24Hours = [];
      })
      .finally(
        action(() => {
          this.getCrimpToolInspectionLast24HoursState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to submit a Start/Pause/Complete state transition for a Crimp Tool Inspection.
  * @param payload: Inspection session payload (State: 1 = Start, 2 = Pause, 3 = Completed)
  */
  AddCrimpToolInspectionService = (payload: IAddCrimpToolInspectionRequest) => {
    this.addCrimpToolInspectionState.inProgress = true;
    return baseService
      .postRequest(URLConstants.AddCrimpToolInspection, payload)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addCrimpToolInspectionState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
          return false;
        }
        this.addCrimpToolInspectionState.success = true;
        return true;
      })
      .catch(() => {
        this.addCrimpToolInspectionState.error = errorMessage.SomethingWentWrong;
        toast.error(formatMessage(errorMessage.SomethingWentWrong));
        return false;
      })
      .finally(
        action(() => {
          this.addCrimpToolInspectionState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to export the crimping inspection record list as a CSV file from API.
  * @param filter: Shared inspection filter values applied to the export
  */
  ExportCrimpingInspectionRecordFileService = (filter?: ISharedInspectionFilter) => {
    this.exportCrimpingInspectionRecordFileState.inProgress = true;
    const filterQuery = buildInspectionFilterQuery(filter);
    const languageQuery = `Language=${encodeURIComponent(preferencesStore.getLanguage)}`;
    const tenantIdQuery = `&TenantId=${Number(getTenantIdFromLocalStorage())}`;
    const url = `${URLConstants.ExportCrimpingInspectionRecordFile}?${languageQuery}${filterQuery}${tenantIdQuery}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.exportCrimpingInspectionRecordFileState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.exportCrimpingInspectionRecordFileState.success = true;
          if (response.data.Data) {
            downloadCsvFromBase64(response.data.Data, 'CrimpingInspectionRecord');
          }
        }
      })
      .catch((err: any) => {
        this.exportCrimpingInspectionRecordFileState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.exportCrimpingInspectionRecordFileState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to reset exportCrimpingInspectionRecordFileState observables to their initial values.
  */
  resetExportFileState = () => {
    this.exportCrimpingInspectionRecordFileState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset addCrimpToolInspectionState observables to their initial values.
  */
  resetAddCrimpToolInspectionState = () => {
    this.addCrimpToolInspectionState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset the store's observables to their initial values.
  */
  reset = () => {
    this.toolCrimpInspectionLast24Hours = [];
    this.getCrimpToolInspectionLast24HoursState = { ...this.initialStateValue };
    this.addCrimpToolInspectionState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset only the crimping inspection record list displayed in the UI.
  */
  resetToolCrimpInspectionList = () => {
    this.toolCrimpInspectionList = getToolCrimpInspectionListInitialState;
  };


  /**
  * This function is used to reset all store observables to their initial values.
  */
  resetStore = () => {
    this.exportCrimpingInspectionRecordFileState = { ...this.initialStateValue };
    this.toolCrimpInspectionList = getToolCrimpInspectionListInitialState;
    this.getAllToolCrimpInspectionState = { ...this.initialStateValue };
    this.toolCrimpInspectionLast24Hours = [];
    this.getCrimpToolInspectionLast24HoursState = { ...this.initialStateValue };
    this.addCrimpToolInspectionState = { ...this.initialStateValue };
  };
}

export default new CrimpingInspectionRecordStore();
